import { useGetIdentity, useGetList } from "ra-core";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import {
  CalendarClock,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  CheckSquare,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import type { Task } from "../types";
import { AddTask } from "./AddTask";
import { TasksListByDueDate } from "./TasksListByDueDate";
import { taskTypeIcon } from "./taskTypeIcons";
import { callNylasApi } from "../providers/supabase/nylasApi";
import { getSupabaseClient } from "../providers/supabase/supabase";

/**
 * Tasks - calendar-first, Google-calendar style: the current month with
 * your tasks laid out on their due dates, and the full grouped list on the
 * right. Meetings and scheduled calls join the calendar once you've
 * connected your own calendar (Settings → My profile, via Nylas) - read
 * live, never stored as tasks (an event has no CRM contact, so it can't be
 * one - see decision #48). Meetings people booked on your booking page
 * (the meetings table) show too, linked to the contact who booked.
 */

interface NylasEventView {
  id: string;
  title: string;
  startTime: string;
  endTime: string;
  allDay: boolean;
}

/** A meeting booked through your booking page (meetings table). */
interface BookedMeetingView {
  id: string;
  title: string;
  invitee_name: string;
  starts_at: string;
  ends_at: string;
  contact_id: number | null;
}

type CalendarEntry = Task | NylasEventView | BookedMeetingView;

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const dayKey = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const itemTime = (item: CalendarEntry) =>
  "due_date" in item
    ? item.due_date
    : "starts_at" in item
      ? item.starts_at
      : item.startTime;

const timeLabel = (iso: string) =>
  new Date(iso).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });

/** One task or calendar event, in the calendar cell or the "+N more" popover. */
const CalendarItem = ({ item }: { item: CalendarEntry }) => {
  if ("due_date" in item) {
    const t = item;
    const Icon = taskTypeIcon(t.type);
    const overdue = !t.done_date && new Date(t.due_date) < new Date();
    return (
      <Link
        to={`/contacts/${t.contact_id}/show`}
        onClick={(e) => e.stopPropagation()}
        title={t.text}
        className={`flex items-center gap-1 rounded px-1 py-0.5 text-[11px] leading-tight truncate border ${
          t.done_date
            ? "border-border text-muted-foreground line-through"
            : overdue
              ? "border-[#c97a63]/50 bg-[#c97a63]/10"
              : "border-[#2e6ba9]/40 bg-[#2e6ba9]/10"
        }`}
      >
        <Icon className="w-3 h-3 shrink-0" />
        <span className="truncate">{t.text}</span>
      </Link>
    );
  }
  if ("starts_at" in item) {
    const m = item;
    const label = `${timeLabel(m.starts_at)} ${m.title} with ${m.invitee_name}`;
    const className =
      "flex items-center gap-1 rounded px-1 py-0.5 text-[11px] leading-tight truncate border border-[#3f8f6e]/50 bg-[#3f8f6e]/10";
    const content = (
      <>
        <CalendarClock className="w-3 h-3 shrink-0" />
        <span className="truncate">{label}</span>
      </>
    );
    return m.contact_id != null ? (
      <Link
        to={`/contacts/${m.contact_id}/show`}
        onClick={(evt) => evt.stopPropagation()}
        title={label}
        className={className}
      >
        {content}
      </Link>
    ) : (
      <div
        onClick={(evt) => evt.stopPropagation()}
        title={label}
        className={className}
      >
        {content}
      </div>
    );
  }
  const e = item;
  return (
    <div
      onClick={(evt) => evt.stopPropagation()}
      title={e.title}
      className="flex items-center gap-1 rounded px-1 py-0.5 text-[11px] leading-tight truncate border border-muted-foreground/30 bg-muted/60 text-muted-foreground"
    >
      <CalendarDays className="w-3 h-3 shrink-0" />
      <span className="truncate">
        {e.allDay
          ? ""
          : `${new Date(e.startTime).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })} `}
        {e.title}
      </span>
    </div>
  );
};

export const TasksPage = () => {
  const { identity } = useGetIdentity();
  const [monthOffset, setMonthOffset] = useState(0);

  const { data: tasks = [] } = useGetList<Task>(
    "tasks",
    {
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "due_date", order: "ASC" },
      filter: { sales_id: identity?.id },
    },
    { enabled: !!identity },
  );

  const today = new Date();
  const monthStart = useMemo(
    () => new Date(today.getFullYear(), today.getMonth() + monthOffset, 1),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [monthOffset],
  );
  const monthLabel = monthStart.toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });

  const [createDate, setCreateDate] = useState<string | null>(null);

  // 6 rows × 7 days starting from the Sunday on/before the 1st
  const cells = useMemo(() => {
    const first = new Date(monthStart);
    first.setDate(1 - first.getDay());
    return Array.from({ length: 42 }, (_, i) => {
      const d = new Date(first);
      d.setDate(first.getDate() + i);
      return d;
    });
  }, [monthStart]);

  // Same "am I connected" check as Settings → My profile's calendar card -
  // without it, the "connect your calendar" hint kept showing even after
  // the user had already connected one.
  const [calendarConnected, setCalendarConnected] = useState<boolean | null>(
    null,
  );
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const { data } = await getSupabaseClient()
          .from("calendar_connections")
          .select("provider")
          .limit(1)
          .maybeSingle();
        if (!cancelled) setCalendarConnected(!!data);
      } catch {
        if (!cancelled) setCalendarConnected(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const [events, setEvents] = useState<NylasEventView[]>([]);
  useEffect(() => {
    const start = cells[0]!;
    const end = cells[cells.length - 1]!;
    let cancelled = false;
    void callNylasApi<{ events: NylasEventView[] }>(
      "/integrations/nylas/events",
      {
        params: { start: start.toISOString(), end: end.toISOString() },
      },
    )
      .then((result) => {
        if (!cancelled) setEvents(result?.events ?? []);
      })
      .catch(() => {
        if (!cancelled) setEvents([]);
      });
    return () => {
      cancelled = true;
    };
  }, [cells]);

  const { data: meetings = [] } = useGetList<BookedMeetingView>(
    "meetings",
    {
      pagination: { page: 1, perPage: 500 },
      sort: { field: "starts_at", order: "ASC" },
      filter: {
        sales_id: identity?.id,
        status: "booked",
        "starts_at@gte": cells[0]!.toISOString(),
        "starts_at@lte": cells[cells.length - 1]!.toISOString(),
      },
    },
    // No meetings table (the demo, or a workspace without the migration):
    // the calendar just shows tasks and events.
    { enabled: !!identity, retry: false },
  );

  const byDay = useMemo(() => {
    const byDay = new Map<string, CalendarEntry[]>();
    const add = (k: string, item: CalendarEntry) =>
      byDay.set(k, [...(byDay.get(k) ?? []), item]);
    for (const t of tasks) add(dayKey(new Date(t.due_date)), t);
    for (const m of meetings) add(dayKey(new Date(m.starts_at)), m);
    // A booked meeting's invite usually lands on the connected calendar
    // too - show it once, as the meeting.
    const bookedSpans = new Set(
      meetings.map(
        (m) => `${Date.parse(m.starts_at)}|${Date.parse(m.ends_at)}`,
      ),
    );
    for (const e of events) {
      if (
        bookedSpans.has(`${Date.parse(e.startTime)}|${Date.parse(e.endTime)}`)
      )
        continue;
      add(dayKey(new Date(e.startTime)), e);
    }
    return byDay;
  }, [tasks, events, meetings]);

  const todayKey = dayKey(today);

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <CheckSquare className="w-5 h-5 text-muted-foreground" />
        <h1 className="text-xl font-semibold flex-1">My tasks</h1>
        <AddTask selectContact />
        {createDate && (
          <AddTask
            key={createDate}
            selectContact
            hideTrigger
            dueDate={createDate}
            externalOpen
            onExternalOpenChange={(o) => {
              if (!o) setCreateDate(null);
            }}
          />
        )}
      </div>

      <div className="grid lg:grid-cols-[minmax(0,1fr)_340px] gap-4 items-start">
        {/* ---------------------------------------------- calendar -------- */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <p className="font-medium">{monthLabel}</p>
              <div className="ml-auto flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Previous month"
                  onClick={() => setMonthOffset((m) => m - 1)}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8"
                  onClick={() => setMonthOffset(0)}
                >
                  Today
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Next month"
                  onClick={() => setMonthOffset((m) => m + 1)}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-7 text-center text-[11px] uppercase tracking-wide text-muted-foreground mb-1">
              {WEEKDAYS.map((d) => (
                <div key={d} className="py-1">
                  {d}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7 border-l border-t border-border rounded-sm overflow-hidden">
              {cells.map((d) => {
                const k = dayKey(d);
                const inMonth = d.getMonth() === monthStart.getMonth();
                const dayItems = byDay.get(k) ?? [];
                const isToday = k === todayKey;
                return (
                  <div
                    key={k}
                    role="button"
                    tabIndex={0}
                    title="Add a task on this day"
                    onClick={() =>
                      setCreateDate(
                        new Date(
                          d.getFullYear(),
                          d.getMonth(),
                          d.getDate(),
                          12,
                        ).toISOString(),
                      )
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter")
                        setCreateDate(
                          new Date(
                            d.getFullYear(),
                            d.getMonth(),
                            d.getDate(),
                            12,
                          ).toISOString(),
                        );
                    }}
                    className={`group min-h-24 border-r border-b border-border p-1 flex flex-col gap-0.5 cursor-pointer transition-colors hover:bg-primary/5 ${
                      inMonth ? "" : "bg-muted/40"
                    }`}
                  >
                    <span
                      className={`text-[11px] tabular-nums self-end px-1 rounded-full ${
                        isToday
                          ? "bg-primary text-primary-foreground font-semibold"
                          : inMonth
                            ? "text-muted-foreground"
                            : "text-muted-foreground/50"
                      }`}
                    >
                      {d.getDate()}
                    </span>
                    {dayItems.slice(0, 3).map((item) => (
                      <CalendarItem
                        key={
                          "due_date" in item
                            ? `task-${item.id}`
                            : `event-${item.id}`
                        }
                        item={item}
                      />
                    ))}
                    {dayItems.length > 3 && (
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            onClick={(e) => e.stopPropagation()}
                            className="text-[10px] text-muted-foreground px-1 text-left hover:underline"
                          >
                            +{dayItems.length - 3} more
                          </button>
                        </PopoverTrigger>
                        <PopoverContent
                          className="w-64 flex flex-col gap-1"
                          align="start"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <p className="text-xs font-medium mb-1">
                            {d.toLocaleDateString("en-US", {
                              weekday: "long",
                              month: "long",
                              day: "numeric",
                            })}
                          </p>
                          {[...dayItems]
                            .sort((a, b) =>
                              itemTime(a).localeCompare(itemTime(b)),
                            )
                            .map((item) => (
                              <CalendarItem
                                key={
                                  "due_date" in item
                                    ? `task-${item.id}`
                                    : `event-${item.id}`
                                }
                                item={item}
                              />
                            ))}
                        </PopoverContent>
                      </Popover>
                    )}
                  </div>
                );
              })}
            </div>

            {calendarConnected === false && (
              <p className="flex items-center gap-1.5 text-[11px] text-muted-foreground mt-3">
                <CalendarDays className="w-3.5 h-3.5" />
                Connect your Google or Microsoft calendar under{" "}
                <Link
                  to="/settings/profile"
                  className="underline hover:no-underline"
                >
                  Settings → My profile
                </Link>{" "}
                and your meetings appear here alongside your tasks.
              </p>
            )}
          </CardContent>
        </Card>

        {/* ---------------------------------------------- list ------------ */}
        <Card>
          <CardContent className="p-4">
            <TasksListByDueDate
              emptyPlaceholder={
                <p className="text-sm text-muted-foreground">
                  Nothing due - add a task to stay ahead of the follow-up.
                </p>
              }
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

TasksPage.path = "/tasks";
